import { Page, View, Text, Link } from '@react-pdf/renderer';
import { styles } from './styles';
import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import { globalStyles } from '../../styles';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { getDescriptionKeyByRegion } from '@utils/regionMapping';
import { APP_HOST } from '@utils/apiHooks';
import { formatCurrency, getImageSrc } from '@utils/fn';
import { SafeImage } from '@composites/PDF/AgendaPdfContent/components/SafeImage';

export interface PackagePageProps {
  packageItem: any;
  event: PublicEventDetailsResponse;
  coLocatedEvents?: PublicEventDetailsResponse[] | undefined;
  footer: JSX.Element;
  currency?: string;
  language?: string | null | undefined;
}

const getList = (rawString: string): string[] =>
  rawString
    .split('\n')
    .map(item => item.trim())
    .filter(item => item.startsWith('-'))
    .map(item => item.slice(1).trim());

const PackagePage = ({
  packageItem,
  event,
  coLocatedEvents,
  footer,
  currency = 'eur',
  language,
}: PackagePageProps) => {
  const { t } = useTranslation();

  const descriptionKey = useMemo(() => {
    if (language?.toString() === 'de') {
      return 'de';
    }

    if (language?.toString() === 'en') {
      if (event?.region === 'usa') {
        return 'us';
      }
      return 'en';
    }

    return getDescriptionKeyByRegion(event?.region || '');
  }, [event?.region, language]);

  const beforeTheEventList = useMemo(() => {
    const desc = packageItem?.description?.[descriptionKey]?.before_the_event;
    return desc ? getList(desc) : [];
  }, [packageItem, descriptionKey]);

  const duringTheEventList = useMemo(() => {
    const desc = packageItem?.description?.[descriptionKey]?.during_the_event;
    return desc ? getList(desc) : [];
  }, [packageItem, descriptionKey]);

  const afterTheEventList = useMemo(() => {
    const desc = packageItem?.description?.[descriptionKey]?.after_the_event;
    return desc ? getList(desc) : [];
  }, [packageItem, descriptionKey]);

  return (
    <Page size={{ width: 1440, height: 800 }} style={styles.page}>
      <View style={styles.columnContainer}>
        <View style={styles.leftColumn}>
          <Text style={{ ...styles.title }}>{packageItem?.name ?? ''}</Text>

          <View
            style={{
              ...globalStyles.lineBar,
              borderColor: event?.design?.themeColor ?? 'black',
              marginLeft: 0,
              marginBottom: 20,
            }}
          />

          <View style={styles.columnContent}>
            <Text style={styles.columnTitle}>{t('before-the-event')}</Text>
            <View
              style={{
                ...globalStyles.lineBar,
                borderColor: 'white',
                marginTop: 10,
                marginLeft: 0,
                marginBottom: 10,
              }}
            />
            {beforeTheEventList.map((item, idx) => (
              <View key={`before-${idx}`} style={styles.bulletRow}>
                <Text style={styles.bullet}>•</Text>
                <Text style={styles.listItem}>{item}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.middleColumn}>
          <View
            style={{
              ...styles.columnContent,
              backgroundColor: event?.design?.themeColor,
              color: 'white',
              overflow: 'hidden',
            }}
          >
            <Text style={{ ...styles.columnTitle, color: 'white' }}>
              {t('at-the-event')}
            </Text>
            <View
              style={{
                ...globalStyles.lineBar,
                borderColor: 'white',
                marginTop: 10,
                marginLeft: 0,
                marginBottom: 10,
              }}
            />
            {duringTheEventList.map((item, idx) => (
              <View key={`during-${idx}`} style={styles.bulletRow}>
                <Text style={{ ...styles.bullet, color: 'white' }}>•</Text>
                <Text style={{ ...styles.listItem, color: 'white' }}>
                  {item}
                </Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.rightColumn}>
          <View style={{ ...styles.columnContent, backgroundColor: 'white' }}>
            <Text style={styles.columnTitle}>{t('after-the-event')}</Text>
            <View
              style={{
                ...globalStyles.lineBar,
                borderColor: '#F5F5F5',
                marginTop: 10,
                marginLeft: 0,
                marginBottom: 10,
              }}
            />
            {afterTheEventList.map((item, idx) => (
              <View key={`after-${idx}`} style={styles.bulletRow}>
                <Text style={styles.bullet}>•</Text>
                <Text style={styles.listItem}>{item}</Text>
              </View>
            ))}
          </View>
          <View style={styles.buyNowWrapper}>
            <Text
              style={{ ...styles.priceText, color: event?.design?.themeColor }}
            >
              {formatCurrency(packageItem?.value, currency || 'eur')}
            </Text>
            <Text style={styles.excludeVATText}>{t('exl-19-vat')}</Text>

            <Link
              src={`${APP_HOST}/events/${String(event?.slug ?? '')}${String(t('partnering-packages-url') ?? '')}/${String(packageItem?.slug ?? '')}`}
              style={{
                ...styles.ticketButton,
                backgroundColor: event?.design?.themeColor || 'black',
              }}
            >
              {t('buy-now')}
            </Link>
          </View>

          {event.colocated && coLocatedEvents && coLocatedEvents.length > 0 && (
            <View style={{ position: 'absolute', bottom: 80 }}>
              <Text
                style={{
                  ...styles.coLocatedWithText,
                  color: event?.design?.themeColor,
                }}
              >
                * {t('co-located-with')}
              </Text>

              <View style={{ ...styles.coLocatedImageWrapper }}>
                {coLocatedEvents
                  ?.filter(coEvent => coEvent.id !== event.id)
                  ?.map(coEvent => (
                    <Link
                      src={`${APP_HOST}/events/${String(coEvent?.slug ?? '')}`}
                      key={coEvent.id}
                    >
                      <SafeImage
                        key={coEvent.id}
                        style={styles.coLocatedImageLogo}
                        src={getImageSrc(coEvent?.images?.logo || '')}
                      />
                    </Link>
                  ))}
              </View>
            </View>
          )}
        </View>
      </View>
      {footer}
    </Page>
  );
};

export default PackagePage;
